import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from "@avenue-8/sidekick-ui";
import { PresentationSkeleton } from "@cma/shared/components/PresentationSkeleton";
import { PresentationView } from "@features/Presentation/PresentationView";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import PrintIcon from "@mui/icons-material/Print";
import { useEffect, useMemo, useState } from "react";
import { useGetCmaPresentationView } from "@cma/shared/queries/cma/useGetCmaPresentationView";
import { Helmet } from "react-helmet-async";
import styled from "@emotion/styled";
import { useAuth } from "@shared/hooks/useAuth";
import { usePageReadySignal } from "@shared/hooks/usePageReadySignal";
import { PresentationThemeProvider } from "@cma/shared/theme/PresentationThemeProvider";
import {
  DEFAULT_PRESENTATION_THEME,
  findThemeById,
  getThemeFontLoadSpecs,
} from "@cma/shared/theme/presentation-themes";

import { Global, css } from "@emotion/react";

const PdfPageStyles = () => (
  <Global
    styles={css`
      @page {
        size: A4 landscape;
        margin: 0;
      }

      @media print {
        html,
        body {
          margin: 0;
        }
      }
    `}
  />
);

const UtilityToolsNavbar = styled.div<{ $position: "fixed" | "static" }>`
  position: ${(p) => p.$position};
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem;
  height: 72px;
  gap: 1rem;
  background: ${(p) => p.theme.colors.background2};
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};

  @media print {
    display: none;
  }
`;

const SmallToggleButtonGroup = styled(ToggleButtonGroup)`
  height: fit-content;

  & .MuiToggleButton-root {
    padding: 0.5rem 1rem;
  }
`;

const NavbarOffset = styled.div`
  height: 72px;
  @media print {
    display: none;
  }
`;

const allowedDisplayModes = ["desktop", "print"] as const;
type DisplayMode = (typeof allowedDisplayModes)[number];

type Props = {
  displayMode?: DisplayMode;
};

export const PresentationViewPage = (props?: Props) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialDisplayMode =
    searchParams.get("displayMode") ?? props?.displayMode ?? "desktop";
  const cmaId = searchParams.get("cmaId");
  const printChunkSectionId = searchParams.get("printChunkSectionId");
  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    allowedDisplayModes.includes(initialDisplayMode as DisplayMode)
      ? (initialDisplayMode as DisplayMode)
      : "desktop",
  );
  const { user } = useAuth();

  const { isLoading, data: presentationView } = useGetCmaPresentationView({
    cmaId: cmaId ?? id ?? "",
  });

  const themeId = presentationView?.themeId ?? DEFAULT_PRESENTATION_THEME.id;
  const themeFontSpecs = useMemo(() => {
    const theme = findThemeById(themeId);
    return theme ? getThemeFontLoadSpecs(theme) : [];
  }, [themeId]);

  usePageReadySignal({
    isReadyToCapture: !isLoading && !!presentationView,
    themeFontSpecs,
  });

  // Simple check for read-only capabilities
  const isSameUser =
    presentationView?.agent.email === user?.email &&
    presentationView?.agent.licenseNumber === user?.mlsId;

  const navigate = useNavigate();

  useEffect(() => {
    if (id || cmaId) return;
    navigate("/404");
  }, [cmaId, id, navigate]);

  const coverPhoto = useMemo(() => {
    if (!presentationView) return null;
    const coverGallery =
      presentationView.galleries[presentationView.subjectProperty.id];
    if (!coverGallery || coverGallery.length === 0) return null;
    return coverGallery[0];
  }, [presentationView]);

  return (
    <div>
      <Helmet>
        <title>Sidekick - CMA Presentation</title>
        <meta
          name="description"
          content={`Sidekick - ${presentationView?.presentationName || "CMA Presentation"}`}
        />
        <meta
          property="og:title"
          content={`Sidekick - ${presentationView?.presentationName || "CMA Presentation"}`}
        />
        <meta
          property="og:description"
          content={
            presentationView?.presentationName ||
            "Professional real estate presentation powered by Sidekick"
          }
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {coverPhoto && (
          <meta
            property="og:image"
            content={coverPhoto.thumbnail || coverPhoto.url}
          />
        )}
      </Helmet>
      <PdfPageStyles />
      <Box margin={"auto"}>
        {isLoading && <PresentationSkeleton />}

        {isSameUser && (
          <>
            <UtilityToolsNavbar
              $position={displayMode === "print" ? "fixed" : "static"}
            >
              <Box>
                <Typography
                  sx={{ margin: 0, fontSize: "1.25rem", fontWeight: 700 }}
                >
                  Presentation View
                </Typography>
                <Chip
                  label="Only you can see this navigation bar"
                  size="small"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
              </Box>
              <Box
                display={"flex"}
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                <Typography fontWeight={700}>Display Mode:</Typography>

                <SmallToggleButtonGroup
                  value={displayMode}
                  exclusive
                  onChange={(_, newMode) => {
                    if (newMode !== null) {
                      setDisplayMode(newMode);
                    }
                  }}
                >
                  <ToggleButton
                    value="desktop"
                    aria-label="desktop mode"
                    size="small"
                  >
                    <DesktopMacIcon sx={{ mr: 0.5 }} />
                    Desktop
                  </ToggleButton>
                  <ToggleButton
                    value="print"
                    aria-label="print mode"
                    size="small"
                  >
                    <PrintIcon sx={{ mr: 0.5 }} />
                    Print
                  </ToggleButton>
                </SmallToggleButtonGroup>
              </Box>
            </UtilityToolsNavbar>
            {displayMode === "print" && <NavbarOffset />}
          </>
        )}

        {!isLoading && presentationView && (
          <PresentationThemeProvider themeId={themeId}>
            <PresentationView
              presentationView={presentationView}
              displayMode={displayMode}
              printChunkSectionId={printChunkSectionId}
            />
          </PresentationThemeProvider>
        )}

        {!isLoading && !presentationView && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            height={"100vh"}
            gap={1}
          >
            <Typography>
              <BrokenImageIcon fontSize="large" />
            </Typography>
            <Typography>No presentation data available</Typography>
          </Box>
        )}
      </Box>
    </div>
  );
};
